import type { ReactNode, KeyboardEvent, MouseEvent, Ref, RefObject } from 'react';
import { Children, isValidElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuX, LuListFilter, LuCheck, LuChevronDown, LuSearch } from 'react-icons/lu';
import Select from './Select';
import { useCelular } from '../hooks/useCelular';
// O FiltrosPainel veste as classes `rp-` do RecordPanel.
import './RecordPanel.css';

/**
 * Peças da linha do molde de lista. O CSS mora em `ListKit.css`.
 *
 * Por que a linha virou `div` e não continuou `button`: ela agora carrega
 * botões de ação dentro, e botão dentro de botão é HTML inválido — o
 * navegador desmonta a árvore em silêncio, sem erro no console. A `div`
 * com `role="button"` + `tabIndex` + Enter/Espaço dá o mesmo teclado que
 * o `button` dava, e deixa os filhos serem botões de verdade.
 */

interface ListRowProps {
    /** Mesmo valor do cabeçalho: as colunas têm que bater. */
    columns: string;
    selected?: boolean;
    /**
     * Clique na linha inteira. **Opcional desde 2026-09-12, e o padrão é não
     * usar**: nas telas de cadastro quem abre o registro é o olho da coluna de
     * ações. Sem isto a linha vira conteúdo comum — sem `role="button"`, sem
     * foco e sem cursor de mão — porque anunciar como botão o que não faz nada
     * é pior do que não anunciar.
     */
    onOpen?: () => void;
    /** Lido por leitor de tela no lugar do conteúdo inteiro da linha. */
    label?: string;
    /**
     * Botão direito na linha (menu de contexto). Opcional: quem não passa não muda — a linha segue
     * com o menu do navegador. A linha ganha foco pelo teclado quando existe, senão Shift+F10 não
     * teria onde acontecer.
     */
    onContextMenu?: (e: MouseEvent<HTMLDivElement>) => void;
    /** A linha com o menu aberto: fica marcada enquanto ele está na frente. */
    marcada?: boolean;
    children: ReactNode;
}

export const ListRow = ({ columns, selected, onOpen, label, onContextMenu, marcada, children }: ListRowProps) => {
    const estilo = { gridTemplateColumns: columns };
    const classe = `lk-row ${onOpen ? 'is-clickable' : ''} ${selected ? 'is-selected' : ''} ${marcada ? 'is-ctx' : ''}`;

    if (!onOpen) {
        return (
            <div
                className={classe}
                style={estilo}
                tabIndex={onContextMenu ? 0 : undefined}
                aria-label={onContextMenu ? label : undefined}
                onContextMenu={onContextMenu}
            >
                {children}
            </div>
        );
    }

    return (
        <div
            role="button"
            tabIndex={0}
            aria-label={label}
            className={classe}
            style={estilo}
            onClick={onOpen}
            onContextMenu={onContextMenu}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
            }}
        >
            {children}
        </div>
    );
};

interface RowActionProps {
    icon: ReactNode;
    /** Vira o `aria-label` e o `title` — o botão é só ícone. */
    label: string;
    onClick: () => void;
    disabled?: boolean;
    danger?: boolean;
}

/**
 * Botão de ação dentro de uma linha. Sempre para a propagação: sem isso
 * o clique sobe para a linha e abre o registro no modo errado.
 */
export const RowAction = ({ icon, label, onClick, disabled, danger }: RowActionProps) => (
    <button
        type="button"
        className={`lk-act ${danger ? 'is-danger' : ''}`}
        aria-label={label}
        title={label}
        disabled={disabled}
        onClick={(e: MouseEvent) => { e.stopPropagation(); onClick(); }}
    >
        {icon}
    </button>
);

/** Célula da última coluna, com as ações encostadas à direita. */
export const RowActions = ({ children }: { children: ReactNode }) => (
    <span className="lk-cell lk-acts-cell">{children}</span>
);

/* ─── A MESMA LISTA NO CELULAR ─────────────────────────────────
   A grade da tabela mede 860px e a tela do telefone tem 362 de área útil: da
   terceira coluna em diante nada aparece, nem a coluna de ações. Abaixo do
   corte (`hooks/useCelular`) a linha vira cartão.

   A regra do que entra, nascida em Conexões (30/09/2026) e confirmada aqui:
   **identidade e estado ficam no cartão, o conteúdo fica um toque adiante**,
   no painel que já ocupa a tela inteira. Título e apoio dizem de quem é a
   linha; a linha de estado diz em que pé está; as ações que sobram são as
   destrutivas, que não cabem dentro do painel sem um segundo passo. */

interface ListCardProps {
    /** Quadrado ou círculo à esquerda: marca, iniciais, ícone do tipo. */
    avatar?: ReactNode;
    /** Quem é esta linha. */
    titulo: ReactNode;
    /** Como se identifica: telefone, e-mail, código, categoria. */
    apoio?: ReactNode;
    /** Em que pé está: status, vencimento, contagem. */
    estado?: ReactNode;
    /** Abrir — o cartão inteiro é o alvo, como a linha é no computador. */
    onOpen?: () => void;
    /** Lido por leitor de tela no lugar do cartão inteiro. */
    label?: string;
    selected?: boolean;
    /** `RowAction` na borda direita, com alvo de toque maior. */
    acoes?: ReactNode;
}

export const ListCard = ({ avatar, titulo, apoio, estado, onOpen, label, selected, acoes }: ListCardProps) => {
    const classe = `lk-card ${onOpen ? 'is-clickable' : ''} ${selected ? 'is-selected' : ''}`;
    const conteudo = (
        <>
            {avatar}
            <span className="lk-card-t">
                <span className="lk-card-n">{titulo}</span>
                {apoio && <span className="lk-card-s">{apoio}</span>}
                {estado && <span className="lk-card-meta">{estado}</span>}
            </span>
            {acoes}
        </>
    );

    if (!onOpen) return <div className={classe}>{conteudo}</div>;

    return (
        <div
            role="button"
            tabIndex={0}
            aria-label={label}
            className={classe}
            onClick={onOpen}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
            }}
        >
            {conteudo}
        </div>
    );
};

/** A pilha de cartões — o que a `.lk-table` é para a tabela. */
export const ListCards = ({ children }: { children: ReactNode }) => (
    <div className="lk-cards">{children}</div>
);

/**
 * A pergunta de remover dentro de um cartão. Empilha, porque a pergunta
 * inteira não cabe numa linha de 320px com dois botões ao lado.
 */
export const CardConfirm = (props: RowConfirmProps) => (
    <div className="lk-card is-confirm">
        <RowConfirm {...props} />
    </div>
);

interface PerPageProps {
    value: number;
    options: readonly number[];
    onChange: (n: number) => void;
}

/**
 * "Mostrar N por página" do rodapé da listagem.
 *
 * Era o mesmo `<select>` nativo copiado em onze telas — virou peça única na
 * padronização de selects (2026-09-12). Tamanho `pill` porque o rodapé é
 * texto corrido com um controle no meio, não formulário: 26px é a altura que
 * a linha já tinha.
 */
export const PerPage = ({ value, options, onChange }: PerPageProps) => (
    <span className="lk-perpage">
        Mostrar
        <Select
            size="pill"
            block={false}
            value={String(value)}
            options={options.map(n => ({ value: String(n), label: String(n) }))}
            onChange={v => onChange(Number(v))}
            ariaLabel="Itens por página"
        />
        por página
    </span>
);

interface RowConfirmProps {
    /** Texto curto, com o nome do registro: `Excluir "Cliente sem interesse"?` */
    question: string;
    confirmLabel?: string;
    onConfirm: () => void;
    onCancel: () => void;
    busy?: boolean;
    /** Uma terceira ação, entre o "Não" e o confirmar, para a alternativa à exclusão. Quem não passa não muda. */
    extraLabel?: string;
    onExtra?: () => void;
}

/**
 * Confirmação de exclusão **na própria linha**.
 *
 * Proposta de 2026-09-12 (mensagens-do-sistema.html), direção A. Substitui o
 * `confirm()` do sistema operacional nas listagens: a pergunta nasce onde o
 * objeto está, e não num diálogo no topo da janela, longe do clique.
 *
 * A linha se pinta e troca as ações pela pergunta — não empurra nada para
 * baixo, porque ocupa a mesma altura e a mesma grade da linha que substitui.
 * Sem overlay, sem salto de foco, sem nada por cima da tela.
 *
 * Exclusão pedida de **dentro de um painel** continua no `<Dialog>`: ali não
 * existe linha para pintar.
 *
 * **Não usa a grade da tabela, e isso é de propósito.** A primeira versão
 * recebia `columns` e punha os botões na coluna de ações — que tem 78px,
 * largura desenhada para dois ÍCONES. Dois botões com texto não cabem: eles
 * transbordavam para a esquerda e ficavam por baixo do texto da pergunta, que
 * então roubava o clique. A faixa de pergunta não tem relação nenhuma com as
 * colunas do registro; é flex, com a pergunta ocupando o que sobra.
 */
export const RowConfirm = ({
    question, confirmLabel = 'Excluir', onConfirm, onCancel, busy = false, extraLabel, onExtra,
}: RowConfirmProps) => (
    <div className="lk-row-confirm" role="alert">
        <span className="lk-confirm-q">{question}</span>
        <button type="button" className="lk-confirm-b" onClick={onCancel} disabled={busy}>
            Não
        </button>
        {extraLabel && onExtra && (
            <button type="button" className="lk-confirm-b" onClick={onExtra} disabled={busy}>
                {extraLabel}
            </button>
        )}
        <button type="button" className="lk-confirm-b is-danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Aguarde…' : confirmLabel}
        </button>
    </div>
);

/* ─── Filtro em pill com popover ──────────────────────────────── */

/**
 * `valor` é a chave guardada no filtro e `rotulo` o que se lê. Sem `rotulo`, os dois são a mesma
 * coisa — é o caso de quase toda tela, que filtra pelo próprio texto da coluna. O Chat separa os
 * dois porque filtra responsável por **id**: pelo nome, dois homônimos virariam uma opção só e
 * renomear o usuário perderia o filtro.
 */
export interface FiltroOpcao { valor: string; total: number; rotulo?: string }

interface FiltroPillProps {
    rotulo: string;
    valor: string | null;
    opcoes: FiltroOpcao[];
    onChange: (v: string | null) => void;
}

/**
 * Filtro da faixa de listagem: fechado é neutro, aplicado carrega o valor
 * escrito e o "x" limpa. Vivia copiado em Contatos, Organizações e Usuários;
 * virou peça do kit em 2026-09-12 quando Oportunidades ia ser a quarta cópia.
 * As três cópias continuam valendo até cada tela trocar pelo import.
 */
export const FiltroPill = ({ rotulo, valor, opcoes, onChange }: FiltroPillProps) => {
    const [aberto, setAberto] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: globalThis.MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    return (
        <div className="lk-filter" ref={ref}>
            <button
                type="button"
                className={`lk-pill ${valor ? 'is-on' : ''}`}
                aria-expanded={aberto}
                onClick={() => (valor ? onChange(null) : setAberto(v => !v))}
            >
                {valor ? <>{rotulo}: <b>{valor}</b> <LuX size={12} /></> : <>{rotulo} ▾</>}
            </button>

            {aberto && !valor && (
                <div className="lk-pop" role="menu">
                    {opcoes.length === 0
                        ? <button type="button" disabled>nada para filtrar</button>
                        : opcoes.map(o => (
                            <button key={o.valor} type="button"
                                onClick={() => { onChange(o.valor); setAberto(false); }}>
                                <span>{o.valor}</span>
                                <span className="lk-pop-count">{o.total}</span>
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
};

/* ─── FILTROS NUM BOTÃO ───────────────────────────────────────────
 * Para a listagem com filtros demais para uma faixa só (Oportunidades,
 * 2026-09-26 — docs/designsystem/propostas/pipeline-filtros.html). A pílula
 * cresce com o valor escolhido; com 7 numa linha sem folga, cada escolha
 * empurrava os vizinhos e o conteúdo pulava. Aqui a faixa guarda só o botão
 * e uma linha de resumo de largura fixa, e os campos moram no painel.
 * Tela com até 3 filtros continua com `FiltroPill`: cabe, e o painel seria
 * um clique a mais sem nada a ganhar.
 *
 * Padrão global desde 2026-10-05: no computador o painel abre COLADO no
 * botão, como a lista de um select, e não mais como gaveta na direita.
 * Montagem de qualquer tela:
 *
 *   const ancora = useRef<HTMLButtonElement>(null);
 *   <FiltrosBotao ref={ancora} ligados={n} aberto={a} onClick={() => setA(v => !v)} />
 *   <FiltrosPainel ancora={ancora} open={a} onClose={() => setA(false)} …>
 *       <FiltroColuna> <FiltroCampo …/> … </FiltroColuna>
 *       <FiltroColuna> … </FiltroColuna>
 *   </FiltrosPainel>
 *
 * Sem `FiltroColuna` os campos ficam numa coluna só.
 */

/** Botão da faixa. A bolinha conta os CAMPOS ligados, não os valores. */
export const FiltrosBotao = ({ ligados, onClick, aberto, ref }: {
    ligados: number;
    onClick: () => void;
    aberto?: boolean;
    /** É a âncora do painel: ele abre logo abaixo deste botão. */
    ref?: Ref<HTMLButtonElement>;
}) => (
    <button ref={ref} type="button" className={`lk-btn lk-fbtn ${ligados ? 'is-on' : ''}`} onClick={onClick} aria-haspopup="dialog" aria-expanded={aberto}>
        <LuListFilter size={14} /> Filtros
        {ligados > 0 && <span className="lk-fbtn-n">{ligados}</span>}
    </button>
);

/**
 * O que está filtrado, numa linha que corta com reticências e nunca quebra.
 *
 * **Não é porta** (13/10/2026, pedido dele): era um `button` com `flex: 1`, então ocupava a faixa
 * inteira à direita do botão Filtros e clicar no VAZIO da linha abria o painel — alvo invisível,
 * sem nada ali que dissesse "clique". Quem abre o painel é o botão, e só ele. O texto completo
 * continua acessível no `title`, para quando a linha corta.
 */
export const FiltrosResumo = ({ partes }: { partes: string[] }) => (
    <span className="lk-fres" title={partes.join(' · ')} aria-hidden={!partes.length}>
        {partes.map((p, i) => (
            <span key={i}>{i > 0 && ' · '}{i === 0 ? <b>{p}</b> : p}</span>
        ))}
    </span>
);

interface FiltrosPainelProps {
    open: boolean;
    onClose: () => void;
    /** Linha de apoio: "Aplicados na hora · 12 de 70 oportunidades". */
    subtitulo: string;
    ligados: number;
    onLimpar: () => void;
    /**
     * No celular o painel vira folha que sobe de baixo e cobre a lista — por
     * isso ganha o "Ver N" (`verRotulo`), que é o jeito de fechar vendo o
     * resultado. No computador a lista fica à vista ao lado e muda na hora.
     *
     * **Não precisa ser passado**: sem valor, o componente decide pela largura
     * da janela. Enquanto era obrigatório, só as Oportunidades passavam, e nas
     * outras telas o painel de 400px abria colado na direita de uma tela de
     * 390.
     */
    celular?: boolean;
    verRotulo?: string;
    /** O botão que abre. No computador o painel nasce logo abaixo dele. */
    ancora: RefObject<HTMLElement | null>;
    children: ReactNode;
}

/** Folga mínima entre o painel e a borda da janela. */
const FPOP_BORDA = 16;

/**
 * Painel dos filtros. No computador é um popover preso ao `FiltrosBotao`, como
 * a lista de um select: sem véu, a lista atrás muda na hora e continua à vista,
 * e clicar fora, Esc ou o próprio botão fecham. Vai para o `body` por portal
 * porque a faixa de filtros mora dentro de caixas que cortam o que transborda;
 * por isso a raiz leva `lk-screen`, que é onde os tokens `--lk-*` moram.
 *
 * No celular continua a folha que sobe de baixo, nas classes `rp-` do
 * RecordPanel: popover numa tela de 390 não teria onde caber.
 */
export const FiltrosPainel = ({ open, onClose, subtitulo, ligados, onLimpar, celular, verRotulo, ancora, children }: FiltrosPainelProps) => {
    const janelaEstreita = useCelular();
    const emFolha = celular ?? janelaEstreita;
    const popRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    // Clicar fora fecha. O botão-âncora fica de fora da conta: ele mesmo
    // alterna, e fechar no mousedown faria o clique reabrir na hora.
    useEffect(() => {
        if (!open || emFolha) return;
        const fora = (e: globalThis.MouseEvent) => {
            const alvo = e.target as Node;
            if (popRef.current?.contains(alvo) || ancora.current?.contains(alvo)) return;
            onClose();
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [open, emFolha, onClose, ancora]);

    // Posição: abaixo do botão, alinhado à esquerda dele; se passar da
    // borda direita da janela, recua o quanto precisa.
    useLayoutEffect(() => {
        if (!open || emFolha) { setPos(null); return; }
        const posicionar = () => {
            const a = ancora.current?.getBoundingClientRect();
            const largura = popRef.current?.offsetWidth ?? 0;
            if (!a) return;
            const maxLeft = window.innerWidth - FPOP_BORDA - largura;
            setPos({ top: Math.round(a.bottom + 6), left: Math.round(Math.max(FPOP_BORDA, Math.min(a.left, maxLeft))) });
        };
        posicionar();
        window.addEventListener('resize', posicionar);
        window.addEventListener('scroll', posicionar, true);
        return () => {
            window.removeEventListener('resize', posicionar);
            window.removeEventListener('scroll', posicionar, true);
        };
    }, [open, emFolha, ancora]);

    if (!open) return null;

    // Campos soltos, sem `FiltroColuna`, viram uma coluna só.
    const temColunas = Children.toArray(children).some(c => isValidElement(c) && c.type === FiltroColuna);
    const corpo = temColunas ? children : <FiltroColuna>{children}</FiltroColuna>;

    if (!emFolha) {
        return createPortal(
            <div
                ref={popRef}
                className="lk-screen lk-fpop"
                role="dialog"
                aria-label="Filtros"
                // Até medir, nasce invisível no canto: o recuo da borda direita
                // depende da largura, que só existe depois de montar.
                style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
            >
                <header className="lk-fpop-head">
                    <span className="lk-fpop-t">Filtros</span>
                    <span className="lk-fpop-sub">{subtitulo}</span>
                    <button type="button" className="lk-fcampo-lp" onClick={onLimpar} disabled={!ligados}>Limpar todos</button>
                </header>
                <div className="lk-fpop-body">{corpo}</div>
            </div>,
            document.body,
        );
    }

    return (
        <div className={`rp-overlay lk-fp-veu ${emFolha ? 'is-sheet' : ''}`} onClick={onClose}>
            <aside className={`rp lk-fp ${emFolha ? 'is-sheet' : ''}`} role="dialog" aria-modal="true" aria-label="Filtros" onClick={e => e.stopPropagation()}>
                {emFolha && <span className="lk-fp-alca" aria-hidden="true" />}
                <header className="rp-head">
                    <div className="rp-id">
                        <h2 className="rp-title">Filtros</h2>
                        <p className="rp-sub">{subtitulo}</p>
                    </div>
                    <button type="button" className="rp-close" onClick={onClose} aria-label="Fechar">
                        <LuX size={14} />
                    </button>
                </header>
                <div className="rp-body lk-fp-body">{corpo}</div>
                <footer className="rp-foot lk-fp-foot">
                    {emFolha ? (
                        <>
                            <button type="button" className="lk-btn" onClick={onLimpar} disabled={!ligados}>Limpar todos</button>
                            <button type="button" className="lk-btn is-primary" onClick={onClose}>{verRotulo ?? 'Ver resultado'}</button>
                        </>
                    ) : (
                        <>
                            <span className="lk-fp-ligados">{ligados === 0 ? 'Nenhum filtro ligado' : ligados === 1 ? '1 filtro ligado' : `${ligados} filtros ligados`}</span>
                            <button type="button" className="lk-btn" onClick={onLimpar} disabled={!ligados}>Limpar todos</button>
                        </>
                    )}
                </footer>
            </aside>
        </div>
    );
};

/**
 * Uma coluna do popover. As colunas têm largura fixa e o campo não muda de
 * coluna quando outro aparece (Campanha, Entre datas) — por isso a divisão é
 * da tela, não do CSS: coluna que se reparte sozinha faz o painel pular.
 * Na folha do celular as colunas viram uma lista só.
 */
export const FiltroColuna = ({ children }: { children: ReactNode }) => (
    <div className="lk-fcol">{children}</div>
);

/** Um campo do painel: rótulo, "Limpar" quando tem valor, e o controle. */
export const FiltroCampo = ({ rotulo, onLimpar, children }: { rotulo: string; onLimpar?: () => void; children: ReactNode }) => (
    <div className="lk-fcampo">
        <div className="lk-fcampo-r">
            <span>{rotulo}</span>
            {onLimpar && <button type="button" className="lk-fcampo-lp" onClick={onLimpar}>Limpar</button>}
        </div>
        {children}
    </div>
);

/**
 * Conjunto pequeno e fixo (temperatura, origem, etapa, período): as opções à
 * vista. Clicar liga e desliga; `unico` troca em vez de somar (período).
 */
export const FiltroChips = ({ opcoes, valores, onChange, unico = false }: {
    opcoes: { valor: string; rotulo?: ReactNode }[];
    valores: string[];
    onChange: (v: string[]) => void;
    unico?: boolean;
}) => (
    <div className="lk-fchips">
        {opcoes.map(o => {
            const on = valores.includes(o.valor);
            const proximo = on ? valores.filter(v => v !== o.valor) : unico ? [o.valor] : [...valores, o.valor];
            return (
                <button key={o.valor} type="button" className={`lk-fchip ${on ? 'is-on' : ''}`} aria-pressed={on} onClick={() => onChange(proximo)}>
                    {o.rotulo ?? o.valor}
                </button>
            );
        })}
    </div>
);

/**
 * Conjunto aberto (responsável, organização, campanha): lista com caixas de
 * marcar, mais de um valor por vez. A lista abre por cima do painel e fica
 * aberta enquanto se marca — fechar é clicar fora ou no próprio campo.
 */
export const FiltroMulti = ({ opcoes, valores, onChange, vazio = 'Todos' }: {
    opcoes: FiltroOpcao[];
    valores: string[];
    onChange: (v: string[]) => void;
    /** Texto do campo sem nada marcado. */
    vazio?: string;
}) => {
    const [aberto, setAberto] = useState(false);
    const [q, setQ] = useState('');
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: globalThis.MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
        };
        // Esc fecha a lista, não o painel: captura antes do ouvinte do painel.
        const esc = (e: globalThis.KeyboardEvent) => {
            if (e.key === 'Escape') { e.stopPropagation(); setAberto(false); }
        };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc, true);
        return () => {
            document.removeEventListener('mousedown', fora);
            document.removeEventListener('keydown', esc, true);
        };
    }, [aberto]);

    useEffect(() => { if (!aberto) setQ(''); }, [aberto]);

    const comBusca = opcoes.length > 7;
    const texto = (o: FiltroOpcao) => o.rotulo ?? o.valor;
    const visiveis = q.trim() ? opcoes.filter(o => texto(o).toLowerCase().includes(q.trim().toLowerCase())) : opcoes;
    const alternar = (v: string) => onChange(valores.includes(v) ? valores.filter(x => x !== v) : [...valores, v]);
    /* O campo fechado mostra o PRIMEIRO marcado, e não o primeiro da lista: procura pela chave
       guardada. Sem isto, filtro por id apareceria como "12" no lugar do nome. */
    const primeiro = valores.length ? (opcoes.find(o => o.valor === valores[0]) ?? null) : null;

    return (
        <div className="lk-fm" ref={ref}>
            <button type="button" className={`lk-fm-campo ${valores.length ? 'is-on' : ''}`} aria-expanded={aberto} onClick={() => setAberto(a => !a)}>
                <span className="lk-fm-txt">{valores.length === 0 ? vazio : primeiro ? texto(primeiro) : valores[0]}</span>
                {valores.length > 1 && <span className="lk-fm-mais">+{valores.length - 1}</span>}
                <LuChevronDown size={13} />
            </button>
            {aberto && (
                <div className="lk-fm-pop" role="listbox" aria-multiselectable="true">
                    {comBusca && (
                        <label className="lk-fm-busca">
                            <LuSearch size={13} />
                            <input autoFocus type="search" placeholder="Procurar…" value={q} onChange={e => setQ(e.target.value)} />
                        </label>
                    )}
                    <div className="lk-fm-lista">
                        {visiveis.length === 0
                            ? <div className="lk-fm-nada">{opcoes.length ? 'Nada com esse nome' : 'Nada para filtrar'}</div>
                            : visiveis.map(o => {
                                const on = valores.includes(o.valor);
                                return (
                                    <button key={o.valor} type="button" role="option" aria-selected={on} className={`lk-fm-op ${on ? 'is-on' : ''}`} onClick={() => alternar(o.valor)}>
                                        <span className="lk-fm-box" aria-hidden="true">{on && <LuCheck size={11} />}</span>
                                        <span className="lk-fm-nome">{texto(o)}</span>
                                        <span className="lk-pop-count">{o.total}</span>
                                    </button>
                                );
                            })}
                    </div>
                </div>
            )}
        </div>
    );
};
